import { Facility } from '@reliance/types/models/Facility';
import { BlockFilters } from '@reliance/types/filters/BlockFilters';
import { ColumnFiltersState } from '@tanstack/react-table';
import moment from 'moment';
import React, { useEffect, useMemo, useRef } from 'react';
import { alertStore } from '../alert/alertStore';
import BlockTable from '../block/Table';
import { useBlocks } from '../hooks/Block';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import { useServerTableFilterState } from '../table/useServerTableFilterState';

type FacilityBlocksTabProps = {
  facility: Facility;
};

function defaultDateRange(): { start: string; end: string } {
  return {
    start: moment().subtract(10, 'year').format('YYYY/MM/DD'),
    end: moment().format('YYYY/MM/DD'),
  };
}

function parseDateRange(v: unknown): { startDate?: string; endDate?: string } | undefined {
  if (!v || typeof v !== 'object') {
    return undefined;
  }
  const o = v as { startDate?: string; endDate?: string };
  if (!o.startDate || !o.endDate) {
    return undefined;
  }
  return o;
}

const COLUMNS = [
  { id: 'blockDate', meta: { sortName: 'blockDate', filterComponent: 'DateRangeFilter' } },
  { id: 'unblockDate', meta: { sortName: 'unblockDate', filterComponent: 'DateRangeFilter' } },
  { id: 'phoneNumber', meta: { sortName: 'account.phoneNumber', filterComponent: 'TextFilter' } },
  { id: 'reason', meta: { sortName: 'reason', filterComponent: 'TextFilter' } },
  { id: 'requestedBy', meta: { sortName: 'requestedBy', filterComponent: 'TextFilter' } },
  { id: 'typeId', meta: { sortName: 'typeId', filterComponent: 'SelectFilter' } },
];

function mergedRecordToBlockFilters(f: Record<string, unknown>, facilityId: number): BlockFilters {
  const blockRange = parseDateRange(f.blockDate);
  const unblockRange = parseDateRange(f.unblockDate);
  const typeRaw = f.typeId;
  const typeId =
    typeRaw != null && String(typeRaw).trim() !== '' && !Number.isNaN(Number(typeRaw))
      ? Number(typeRaw)
      : undefined;
  return {
    facilityId,
    includeUnblocked: true,
    page: Number(f.page) || 1,
    perPage: Number(f.perPage) || 100,
    orderBy: typeof f.orderBy === 'string' && f.orderBy.trim() !== '' ? f.orderBy : '-blockDate',
    startDate: blockRange?.startDate
      ? moment(blockRange.startDate, 'YYYY/MM/DD').startOf('day').format('YYYY/MM/DD HH:mm:ss')
      : undefined,
    endDate: blockRange?.endDate
      ? moment(blockRange.endDate, 'YYYY/MM/DD').endOf('day').format('YYYY/MM/DD HH:mm:ss')
      : undefined,
    startUnblockDate: unblockRange?.startDate
      ? moment(unblockRange.startDate, 'YYYY/MM/DD').startOf('day').format('YYYY/MM/DD HH:mm:ss')
      : undefined,
    endUnblockDate: unblockRange?.endDate
      ? moment(unblockRange.endDate, 'YYYY/MM/DD').endOf('day').format('YYYY/MM/DD HH:mm:ss')
      : undefined,
    phoneNumber: f.phoneNumber != null ? String(f.phoneNumber).trim() || undefined : undefined,
    reason: f.reason != null ? String(f.reason).trim() || undefined : undefined,
    requestedBy: f.requestedBy != null ? String(f.requestedBy).trim() || undefined : undefined,
    typeId,
  };
}

const FacilityBlocksTab: React.FC<FacilityBlocksTabProps> = ({ facility }) => {
  const initialColumnFilters = useMemo<ColumnFiltersState>(() => {
    const dateRange = defaultDateRange();
    return [
      { id: 'blockDate', value: { startDate: dateRange.start, endDate: dateRange.end } },
      { id: 'unblockDate', value: { startDate: dateRange.start, endDate: dateRange.end } },
    ];
  }, [facility.id]);

  const baseRecord = useMemo(() => ({}), []);
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 100 },
    initialSorting: [{ id: 'blockDate', desc: true }],
    initialColumnFilters,
  });

  const skipFacilityResetOnMount = useRef(true);
  useEffect(() => {
    if (skipFacilityResetOnMount.current) {
      skipFacilityResetOnMount.current = false;
      return;
    }
    const dateRange = defaultDateRange();
    tableState.setPagination({ pageIndex: 0, pageSize: 100 });
    tableState.setSorting([{ id: 'blockDate', desc: true }]);
    tableState.setColumnFilters([
      { id: 'blockDate', value: { startDate: dateRange.start, endDate: dateRange.end } },
      { id: 'unblockDate', value: { startDate: dateRange.start, endDate: dateRange.end } },
    ]);
  }, [facility.id]);

  const filters = useMemo(
    () => mergedRecordToBlockFilters(tableState.mergedRecord, facility.id),
    [tableState.mergedRecord, facility.id]
  );

  const result = useBlocks(filters);

  useNotifyTabLoading('blocks', result.loading);

  useEffect(() => {
    if (result.error) {
      alertStore.error('An error occurred while loading blocks');
    }
  }, [result.error]);

  const data = result.result;
  const total = data?.total ?? 0;
  const perPage = data?.perPage ?? filters.perPage ?? 100;
  const totalPages = data?.totalPages ?? Math.max(1, Math.ceil(total / (perPage || 1)));

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <BlockTable
          key={facility.id}
          variant="facility"
          loading={result.loading}
          blocks={data?.blocks ?? []}
          page={data?.page ?? filters.page ?? 1}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          serverPagination={tableState.serverPagination}
          onServerPaginationChange={tableState.onServerPaginationChange}
          serverSorting={tableState.serverSorting}
          onServerSortingChange={tableState.onServerSortingChange}
          serverColumnFilters={tableState.serverColumnFilters}
          onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
        />
      </div>
    </div>
  );
};

export default FacilityBlocksTab;
